// ./useTokioData.ts

import * as React from "react";
import { useEffect, useState } from "react";
import { FireServices } from "../../../../../shared/services/api/fire/FireServices";
import { AssistanceItemData, ClientFireSettingsPayload, CoverageGroup, InsurerDetail } from "../../../../../shared/services/api/fire/types";
import { mapApiToDetailedItem, mapApiToItem } from "../../../utils/tokioMappers";

// Tipos e funções auxiliares permanecem inalterados
export type TokioFormData = ClientFireSettingsPayload & {
	agravoResidencial: string;
	agravoComercial: string;
	descontoResidencial: string;
	descontoComercial: string;
	prolabore: string;
	prolaboreMin: string;
	prolaboreMax: string;
	prolaborePadrao: string;
	coverageType: string;
};

interface UseTokioDataProps {
	clientId: string;
	insurerId: string;
	page?: number;
	limit?: number;
	shouldLoad: boolean;
	refetchKey: number;
}

interface UseTokioDataResult {
	isLoading: boolean;
	setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
	initialData: Partial<TokioFormData> | null;
	assistancesResidential: AssistanceItemData[];
	assistancesCommercial: AssistanceItemData[];
	initialCoverages: CoverageGroup[];
	hasLoadError: boolean;
	setAssistancesResidential: React.Dispatch<React.SetStateAction<AssistanceItemData[]>>;
	setAssistancesCommercial: React.Dispatch<React.SetStateAction<AssistanceItemData[]>>;
}

const formatPercentString = (value: string | undefined): string => {
	return (value ?? "0.00").replace(".", ",");
};

const determineCoverageType = (predio: string | undefined, conteudo: string | undefined): string => {
	const pPredio = (predio ?? "0.00").trim();
	const pConteudo = (conteudo ?? "0.00").trim();

	if (pPredio === "90.00" && pConteudo === "10.00") return "Predio_e_Conteudo_90_10";
	if (pPredio === "80.00" && pConteudo === "20.00") return "Predio_e_Conteudo_80_20";
	if (pPredio === "70.00" && pConteudo === "30.00") return "Predio_e_Conteudo_70_30";

	return "Predio_e_Conteudo_Outras";
};

const mapDetailToSettings = (detail: InsurerDetail): Partial<TokioFormData> => {
	const predialRaw = detail.pc_predio;
	const conteudoRaw = detail.pc_conteudo;

	return {
		clientId: detail.clientId ?? "",
		insurance_companyId: detail.insurance_companyId ?? "",
		user_insurance_companies: detail.user_insurance_companies ?? "",
		password_insurance_companies: detail.password_insurance_companies ?? "",
		integrator_code: detail.integrator_code ?? "",
		transfer_type: detail.transfer_type ?? "broker",
		status: "active",
		installment_number: detail.installment_number !== undefined ? Number(detail.installment_number) : 1,
		pc_predio: formatPercentString(predialRaw),
		pc_conteudo: formatPercentString(conteudoRaw),
		comission_insurance_companies: detail.comission_insurance_companies ?? "0.00",
		comission_broker: detail.comission_broker ?? "0.00",
		coverageType: determineCoverageType(predialRaw, conteudoRaw),
		agravoResidencial: "",
		agravoComercial: "",
		descontoResidencial: "",
		descontoComercial: "",
		prolabore: "",
		prolaboreMin: "",
		prolaboreMax: "",
		prolaborePadrao: "",
	};
};

export const useTokioData = ({ clientId, insurerId, shouldLoad, refetchKey }: UseTokioDataProps): UseTokioDataResult => {
	const [isLoading, setIsLoading] = useState(false);
	const [hasLoadError, setHasLoadError] = useState(false);
	const [initialData, setInitialData] = useState<Partial<TokioFormData> | null>(null);
	const [assistancesResidential, setAssistancesResidential] = useState<AssistanceItemData[]>([]);
	const [assistancesCommercial, setAssistancesCommercial] = useState<AssistanceItemData[]>([]);
	const [initialCoverages, setInitialCoverages] = useState<CoverageGroup[]>([]);
	const isFirstLoad = React.useRef(true);

	useEffect(() => {
		if (!shouldLoad) {
			setInitialData(null);
			setHasLoadError(false);
			if (isFirstLoad.current) {
				setAssistancesResidential([]);
				setAssistancesCommercial([]);
				setInitialCoverages([]);
			}
			setIsLoading(false);
			return;
		}

		let active = true;
		setIsLoading(true);
		setHasLoadError(false);

		const loadTokioParametrization = async () => {
			const detailResponse = await FireServices.getAllCompanyDetailById(clientId, insurerId);

			if (detailResponse instanceof Error || !active) {
				console.error("Erro ao carregar detalhes principais:", detailResponse);
				setInitialData(null);
				setHasLoadError(true);
				setIsLoading(false);
				isFirstLoad.current = false;
				return;
			}

			setHasLoadError(false);

			const isResponseEmpty = !detailResponse || (Array.isArray(detailResponse) && detailResponse.length === 0);

			let dataToMap: InsurerDetail | undefined;

			if (isResponseEmpty) {
				setInitialData(null);
			} else {
				dataToMap = Array.isArray(detailResponse) ? detailResponse[0] : detailResponse;
				setInitialData(mapDetailToSettings(dataToMap as InsurerDetail));
			}

			// EXTRAÇÃO DE DADOS CONSOLIDADOS
			if (dataToMap) {
				const rawAssistances = (dataToMap as any).fireClientAssistances || [];
				const allAssistances: AssistanceItemData[] = rawAssistances.map(mapApiToItem);

				const res = allAssistances.filter((x) => x.category === "residential");
				const com = allAssistances.filter((x) => x.category === "commercial");

				setAssistancesResidential(res);
				setAssistancesCommercial(com);

				const rawCoverages = (dataToMap as any).fireClientCoverages || [];
				const mappedCoverages = mapApiToDetailedItem(rawCoverages);

				setIsLoading(false);
				setInitialCoverages(mappedCoverages);
			} else {
				setAssistancesResidential([]);
				setAssistancesCommercial([]);
				setInitialCoverages([]);
			}

			if (active) {
				setIsLoading(false);
				isFirstLoad.current = false;
			}
		};

		loadTokioParametrization();

		return () => {
			active = false;
		};
	}, [clientId, insurerId, shouldLoad, refetchKey]);

	return {
		isLoading,
		setIsLoading,
		initialData,
		assistancesResidential,
		assistancesCommercial,
		initialCoverages,
		hasLoadError,
		setAssistancesResidential,
		setAssistancesCommercial,
	};
};
